import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, Info, TrendingDown, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice } from '../utils/formatters';
import { listingService } from '../services/listingService';
import { categoryService } from '../services/categoryService';
import { uploadMediaFiles } from '../services/uploadService';

const MapPicker = lazy(() => import('../components/map/MapPicker'));

const CONDITIONS_TR = ['Sıfır', 'Mükemmel', 'Çok İyi', 'İyi', 'Kabul Edilebilir'];

export default function CreateListing() {
  const { user } = useAuth();
  const { t, tCat, tCond } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [categories, setCategories] = useState([]);
  const [createdListing, setCreatedListing] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', categoryId: '', condition: '',
    startPrice: '', floorPrice: '', dropAmount: '', dropInterval: '8',
    city: '', district: '', shippingCost: '',
    tags: '', images: [],
    latitude: null, longitude: null,
  });

  useEffect(() => {
    categoryService.getAll().then(setCategories).catch(() => {});
  }, []);

  if (!user) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <h2 className="text-2xl font-bold text-apple-gray-dark mb-4">{t('create.need_login')}</h2>
      <p className="text-apple-gray-mid mb-6">{t('create.need_login_desc')}</p>
      <Link to="/login" className="btn-primary">{t('login.submit')}</Link>
    </div>
  );

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const previewPrice = () => {
    const start = parseFloat(form.startPrice) || 0;
    const floor = parseFloat(form.floorPrice) || 0;
    const drop = parseFloat(form.dropAmount) || 0;
    const interval = parseInt(form.dropInterval) || 8;
    const steps = drop > 0 ? Math.floor((start - floor) / drop) : 0;
    const hours = steps * interval;
    const days = Math.floor(hours / 24);
    return { start, floor, drop, interval, steps, hours, days };
  };

  const p = previewPrice();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        categoryId: form.categoryId,
        condition: form.condition,
        startPrice: parseFloat(form.startPrice),
        floorPrice: parseFloat(form.floorPrice),
        dropAmount: parseFloat(form.dropAmount),
        dropIntervalHours: parseInt(form.dropInterval),
        city: form.city,
        district: form.district || null,
        latitude:  form.latitude  ?? null,
        longitude: form.longitude ?? null,
        shippingCost: form.shippingCost !== '' ? parseFloat(form.shippingCost) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        images: form.images || [],
      };
      const result = await listingService.create(payload);
      setCreatedListing(result);
      setSubmitted(true);
      setTimeout(() => navigate('/my-listings'), 2500);
    } catch (err) {
      setApiError(err?.response?.data?.message || t('create.default_error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center animate-slide-up">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-apple-green" />
      </div>
      <h2 className="text-3xl font-bold text-apple-gray-dark mb-3">{t('create.success_title')}</h2>
      <p className="text-apple-gray-mid mb-2">{t('create.success_msg')}</p>
      {createdListing?.listingNo && <p className="text-sm font-medium text-apple-blue">#{createdListing.listingNo}</p>}
      <p className="text-sm text-apple-gray-mid mt-2">{t('create.success_redirect')}</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-apple-gray-dark mb-1">{t('create.page_title')}</h1>
        <p className="text-apple-gray-mid">{t('create.page_subtitle')}</p>
      </div>

      <div className="flex items-center gap-0 mb-10">
        {[1, 2, 3].map((s, i) => (
          <React.Fragment key={s}>
            <button type="button" onClick={() => step > s && setStep(s)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${step === s ? 'bg-apple-blue text-white' : step > s ? 'bg-apple-green text-white cursor-pointer' : 'bg-apple-gray text-apple-gray-mid'}`}>
              {step > s ? '✓' : s} {[t('create.step1'), t('create.step2'), t('create.step3')][i]}
            </button>
            {i < 2 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-apple-green' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {apiError && (
        <div className="flex items-center gap-2 bg-red-50 text-apple-red text-sm p-3 rounded-xl mb-6">
          <AlertCircle className="w-4 h-4 shrink-0" /> {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-5 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.title_label')} *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="ör: iPhone 14 Pro Max 256GB Space Black" className="input-field" required />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.category_label')} *</label>
                <select value={form.categoryId} onChange={e => set('categoryId', e.target.value)} className="input-field" required>
                  <option value="">{t('create.select')}</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{tCat(c.slug)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.condition_label')} *</label>
                <select value={form.condition} onChange={e => set('condition', e.target.value)} className="input-field" required>
                  <option value="">{t('create.select')}</option>
                  {CONDITIONS_TR.map(c => <option key={c} value={c}>{tCond(c)}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.desc_label')} *</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5} placeholder="..." className="input-field resize-none" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.tags_label')}</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder={t('create.tags_placeholder')} className="input-field" />
            </div>

            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.photos_label')}</label>
              <label className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-apple-blue transition-colors cursor-pointer block">
                <Upload className="w-8 h-8 text-apple-gray-mid mx-auto mb-2" />
                <p className="text-sm text-apple-gray-mid">{t('create.photo_click')}</p>
                <p className="text-xs text-apple-gray-mid mt-1">{t('create.photo_hint')}</p>
                <input
                  type="file"
                  accept="image/*,.gif,.webp,.glb,.usdz"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files).slice(0, 10);
                    try {
                      const urls = await uploadMediaFiles(files);
                      set('images', urls);
                    } catch {
                      setApiError(t('create.upload_error') || 'Fotoğraf yüklenemedi.');
                    }
                  }}
                />
              </label>
              {form.images.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative">
                      <img src={src} alt="" className="w-16 h-16 object-cover rounded-xl border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => set('images', form.images.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-apple-red text-white rounded-full text-xs flex items-center justify-center"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="button" onClick={() => setStep(2)} className="btn-primary w-full py-3.5 text-base">
              {t('create.next_price')}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-in">
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
              <TrendingDown className="w-5 h-5 text-apple-blue shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-apple-blue mb-1">{t('create.dutch_title')}</p>
                <p className="text-xs text-blue-700 leading-relaxed">{t('create.dutch_desc')}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.start_price')} *</label>
                <input type="number" value={form.startPrice} onChange={e => set('startPrice', e.target.value)} placeholder="500" className="input-field" min="1" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.floor_price')} *</label>
                <input type="number" value={form.floorPrice} onChange={e => set('floorPrice', e.target.value)} placeholder="300" className="input-field" min="1" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.drop_amount')} *</label>
                <input type="number" value={form.dropAmount} onChange={e => set('dropAmount', e.target.value)} placeholder="20" className="input-field" min="1" required />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.drop_interval')} *</label>
              <div className="flex gap-2 flex-wrap">
                {[2, 4, 6, 8, 12, 24, 48].map(h => (
                  <button key={h} type="button" onClick={() => set('dropInterval', String(h))}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${form.dropInterval === String(h) ? 'bg-apple-blue text-white border-apple-blue' : 'border-gray-200 text-apple-gray-mid hover:border-apple-blue'}`}>
                    {h}{t('create.hours_unit')}
                  </button>
                ))}
                <input type="number" value={form.dropInterval} onChange={e => set('dropInterval', e.target.value)} className="input-field w-20 text-center" min="1" max="168" />
              </div>
            </div>

            {p.start > 0 && p.floor > 0 && p.drop > 0 && (
              <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-5 border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-apple-blue" />
                  <span className="text-sm font-semibold text-apple-gray-dark">{t('create.preview')}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-xl p-3 text-center"><p className="text-xs text-apple-gray-mid">{t('create.prev_start')}</p><p className="font-bold text-apple-gray-dark">{formatPrice(p.start)}</p></div>
                  <div className="bg-white rounded-xl p-3 text-center"><p className="text-xs text-apple-gray-mid">{t('create.prev_floor')}</p><p className="font-bold text-apple-green">{formatPrice(p.floor)}</p></div>
                  <div className="bg-white rounded-xl p-3 text-center"><p className="text-xs text-apple-gray-mid">{t('create.prev_total')}</p><p className="font-bold text-apple-red">-{formatPrice(p.start - p.floor)}</p></div>
                  <div className="bg-white rounded-xl p-3 text-center"><p className="text-xs text-apple-gray-mid">{t('create.prev_duration')}</p><p className="font-bold text-apple-gray-dark">{p.days > 0 ? `${p.days} ${t('create.days')}` : `${p.hours} ${t('create.hours_unit')}`}</p></div>
                </div>
                <p className="text-xs text-apple-gray-mid text-center mt-3">
                  {t('create.prev_desc').replace('{0}', p.interval).replace('{1}', formatPrice(p.drop)).replace('{2}', p.steps)}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1 py-3.5">{t('create.back')}</button>
              <button type="button" onClick={() => setStep(3)} className="btn-primary flex-1 py-3.5">{t('create.next_location')}</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.city_label')} *</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Berlin" className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.district_label')}</label>
                <input value={form.district} onChange={e => set('district', e.target.value)} placeholder="Mitte" className="input-field" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('create.shipping_label')}</label>
              <input type="number" value={form.shippingCost} onChange={e => set('shippingCost', e.target.value)} placeholder="0" className="input-field" min="0" step="0.01" />
              <p className="text-xs text-apple-gray-mid mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" /> {t('create.shipping_hint')}
              </p>
            </div>

            {/* Konum seçici */}
            <div>
              <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">
                Konum <span className="text-apple-gray-mid font-normal">(isteğe bağlı)</span>
              </label>
              <Suspense fallback={<div className="h-80 bg-apple-gray rounded-2xl animate-pulse" />}>
                <MapPicker
                  value={form.latitude && form.longitude ? { lat: form.latitude, lng: form.longitude } : null}
                  onChange={(pos) => {
                    set('latitude',  pos?.lat ?? null);
                    set('longitude', pos?.lng ?? null);
                  }}
                />
              </Suspense>
            </div>

            <div className="bg-apple-gray rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-apple-gray-dark mb-3">{t('create.summary_title')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-apple-gray-mid">{t('create.sum_title')}:</span><span className="font-medium text-apple-gray-dark truncate max-w-xs">{form.title || '-'}</span></div>
                <div className="flex justify-between"><span className="text-apple-gray-mid">{t('create.sum_category')}:</span><span className="font-medium text-apple-gray-dark">{categories.find(c => String(c.id) === String(form.categoryId)) ? tCat(categories.find(c => String(c.id) === String(form.categoryId)).slug) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-apple-gray-mid">{t('create.sum_start')}:</span><span className="font-bold text-apple-gray-dark">{form.startPrice ? formatPrice(parseFloat(form.startPrice)) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-apple-gray-mid">{t('create.sum_floor')}:</span><span className="font-bold text-apple-green">{form.floorPrice ? formatPrice(parseFloat(form.floorPrice)) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-apple-gray-mid">{t('create.sum_drop')}:</span><span className="text-apple-red">{form.dropAmount ? `-${formatPrice(parseFloat(form.dropAmount))} / ${form.dropInterval}${t('create.hours_unit')}` : '-'}</span></div>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1 py-3.5">{t('create.back')}</button>
              <button type="submit" disabled={submitting} className="btn-primary flex-1 py-3.5 text-base font-semibold disabled:opacity-60">
                {submitting ? t('create.publishing') : t('create.publish')}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
